/**
 * JavaScript port of IngredientParser.java and UnitRegistry.java (fizapilib, package
 * com.jeronimo.fiz.mealplanner), reading the same units.json: the copy next to this file is verbatim,
 * keep it in sync — the point of sharing the table is that all clients recognize and output exactly
 * the same units. Splits a raw ingredient line into {name, quantity, unit}.
 *
 * The line is NOT tokenized on whitespace, because units can be multi-word ("cuillère à soupe",
 * "c. à soupe", "fluid ounce"). Instead we:
 *   1. find the quantity anywhere in the line via a regex (integer, decimal, simple/mixed/unicode
 *      fraction, range) — a trailing "times" x ("2x", "2 x") is consumed and dropped; when no digit
 *      is present, a spelled-out small number 1/2/3 ("two", "deux", "tre") is tried instead;
 *   2. if (and only if) a quantity was found, look for a unit phrase known to the UnitRegistry,
 *      matched whole-word and accent/case insensitively. The one nearest the quantity wins, and one
 *      sitting right after another number is a size annotation that loses to any other candidate
 *      ("1 (14.5 ounce) can tomatoes" -> can, "60 ml (1/4 tasse) de lait" -> ml);
 *   3. cut both spans out of the original text — whatever remains is the name.
 *
 * Without a quantity nothing is a unit: "Pinch of salt" and "Jalapeño slices" are names, in full.
 * A localeHint (en/fr/de/es/it/nl) is preferred when resolving word-numbers and ambiguous unit forms;
 * without one, locales are tried in order en, fr, de, es, it, nl. The locale a unit form matched in
 * also decides the output for an untranslated unit ("2 Flaschen" -> Flasche, "2 bouteilles" ->
 * bouteille); see unitOutput().
 * Complements / parentheses are deliberately left untouched: "2 x cans (400g) crushed tomatoes"
 * keeps "(400g)" inside the name.
 *
 * Examples: "1 tsp chili flakes" -> name="chili flakes", qty=1, unit=tsp;
 * "tomate 2x" -> name="tomate", qty=2, unit=pc (no unit written = a count of pieces).
 *
 * Cases we are not handling:
 *   1 (20 g) Mehl                     | 1    | g | (20 ) Mehl (only candidate, so it wins despite the number before it)
 *   2x500g Woolworths soup vegetables | 2    |   | x500g Woolworths soup vegetables
 *   Roggenmehl Typ 1150: 100 g        | 1150 | g | Roggenmehl Typ : 100
 *   pinch ground cloves (no quantity, so no unit, but "cloves" is the spice not the unit)
 *
 * Two things this file has and the Java has not: addUnit()/registerUnit(), for a unit the user typed
 * in the quantity picker, and checkUnits(), the validation UnitRegistry.java runs while loading
 * units.json — here it is the unit test's job, since a table we ship broken must not blank out a page.
 */

//esbuild and node both read a json import; the import attribute is what node insists on
import UNITS_JSON from "./units.json" with { type: "json" };

/** Fallback unit for a bare count ("2 eggs"): the units.json key for a piece (a translated unit). */
export const PIECE = 'pc';

/** One locale of a unit: its canonical/display form, and every form to recognize (eq holds unitName). */
export type UnitLocale = {
	unitName: string;
	eq: string[];
};

/**
 * One unit as declared in units.json, its forms keyed by 2-letter language code.
 *
 * unitKey is the unit's identity, an abbreviation such as "g", "tbsp", "can". translated tells what a
 * client is expected to display, and therefore what the parser outputs (see unitOutput()):
 *  - true for the measures (g, kg, ml, tbsp, cup, pc...): the parser yields the locale-independent
 *    unitKey and the client localizes it — the per-locale unitName is exactly that label;
 *  - false for the container-ish units (can, btl, box, clv, pnch...): the parser yields the unitName
 *    of the locale the form was written in, so the client has nothing to translate. This is
 *    deliberate: those words are ambiguous across languages ("boîte" is both a can and a carton) and
 *    awkward in a unit dropdown.
 *
 * The declaration order of the locales is significant: it decides the winning spelling for a form
 * shared by two locales (see buildAliases), and json objects keep their file order.
 */
export type UnitDef = {
	unitKey: string;
	translated: boolean;
	values: Record<string, UnitLocale>;
};

/** units.json, typed: json only carries string/boolean/array, its shape is ours to declare. */
const UNITS = UNITS_JSON as UnitDef[];

/**
 * What a raw ingredient line splits into.
 *
 * unit is what unitOutput() yields for the matched form: the locale-independent unitKey for a
 * translated unit ("kg", "tbsp"), the word as the unit is spelled in the locale it was written in for
 * an untranslated one ("bouteille", "Dose", "clove").
 * unit is null when no quantity is found (a unit word without a quantity belongs to the name); when a
 * quantity is found the unit is never null — it defaults to PIECE.
 */
export type ParsedIngredient = {
	name: string;
	quantity: number | null;
	unit: string | null;
};

/**
 * What to store/display for a form found in the given locale: the unitKey for a translated unit, the
 * locale's unitName otherwise.
 * @param locale the 2-letter code of the locale whose form matched; unknown falls back to en.
 */
export function unitOutput(def: UnitDef, locale: string): string {
	if (def.translated) {
		return def.unitKey;
	}
	const hit = def.values[locale] ?? def.values['en'];
	return hit ? hit.unitName : def.unitKey;
}

/**
 * In-memory index of the measurement units of units.json, equivalent to UnitRegistry.java.
 * From any written form ("EL", "cuillères à soupe", "latas") you can retrieve the unitKey(s).
 */
export class UnitRegistry {

	static #instance: UnitRegistry;

	constructor(definitions: UnitDef[] = UNITS) {
		this.#definitions = [...definitions];
		this.#aliasToUnitKeys = new Map();
		for (const def of this.#definitions) {
			this.#index(def);
		}
	}

	#definitions: UnitDef[];
	// normalized form -> unitKeys (a form like "pot" is declared by several locales, "gr" by several units)
	#aliasToUnitKeys: Map<string, Set<string>>;

	static get(): UnitRegistry {
		UnitRegistry.#instance ??= new UnitRegistry();
		return UnitRegistry.#instance;
	}

	#index(def: UnitDef){
		for (const value of Object.values(def.values)) {
			for (const form of value.eq) {
				const key = normalizeForm(form);
				if (!key) {
					continue;
				}
				if (!this.#aliasToUnitKeys.has(key)) {
					this.#aliasToUnitKeys.set(key, new Set());
				}
				this.#aliasToUnitKeys.get(key).add(def.unitKey);
			}
		}
	}

	/**
	 * Find the unit key(s) matching a written unit form, case- and accent-tolerant.
	 * @returns matching unitKeys (usually one), or empty if the token is not a known unit.
	 */
	findUnitNames(token: string): Set<string> {
		if (token == null) {
			return new Set();
		}
		return new Set(this.#aliasToUnitKeys.get(normalizeForm(token)) ?? []);
	}

	/** @returns true if the token is a known unit in any locale. */
	isUnit(token: string): boolean {
		return this.findUnitNames(token).size > 0;
	}

	/** All units, in file order. */
	definitions(): UnitDef[] {
		return this.#definitions;
	}

	/** The unit of a key, or undefined for a unitKey we do not know. */
	definition(unitKey: string): UnitDef | undefined {
		return this.#definitions.find((def) => def.unitKey === unitKey);
	}

	/** What to store/display for a unit in a locale: see unitOutput(). An unknown key is left as-is. */
	output(unitKey: string, locale: string): string {
		const def = this.definition(unitKey);
		return def ? unitOutput(def, locale) : unitKey;
	}

	/**
	 * Declare a unit the table does not know, typed by the user in the quantity picker. Translated on
	 * purpose: what the user typed is what every locale shows, we have nothing to translate it into.
	 * @returns false when the form was already known, nothing was added.
	 */
	addUnit(unitName: string, locale = 'en'): boolean {
		const
			written = unitName.trim(),
			key = normalizeForm(written);

		if (!key || this.#aliasToUnitKeys.has(key)) {
			return false;
		}
		const def: UnitDef = {
			unitKey: written,
			translated: true,
			values: {[locale]: {unitName: written, eq: [written]}},
		};
		this.#definitions.push(def);
		this.#index(def);
		return true;
	}
}

/**
 * The checks UnitRegistry.java runs while loading units.json, which the browser has no reason to pay
 * for: the table is hand-edited, so it is the unit test that must fail rather than a family's page.
 * @returns the problems found, empty when the table is sound.
 */
export function checkUnits(definitions: UnitDef[] = UNITS): string[] {
	const
		problems: string[] = [],
		keys = new Set<string>(),
		check = (condition: boolean, message: string) => {
			if (!condition) {
				problems.push('units.json: ' + message);
			}
		};

	for (const def of definitions) {
		check(!!def.unitKey?.trim(), 'a unit has no unitKey');
		check(!keys.has(def.unitKey), 'duplicate unitKey ' + def.unitKey);
		keys.add(def.unitKey);
		check(typeof def.translated === 'boolean', def.unitKey + ': translated must be true or false');
		check(!!def.values?.['en'], def.unitKey + ' has no en forms');
		for (const [locale, value] of Object.entries(def.values ?? {})) {
			const where = def.unitKey + '/' + locale;
			check(!!value.unitName?.trim(), where + ' has no unitName');
			check(!!value.eq?.length, where + ' has no eq form');
			check(!!value.eq?.some((form) => normalizeForm(form) === normalizeForm(value.unitName)),
				where + ': eq must contain unitName ' + value.unitName);
			for (const form of value.eq ?? []) {
				check(!!normalizeForm(form), where + ' has a blank eq form');
			}
		}
	}
	return problems;
}

// ½ ¼ ¾ ⅓ ⅔ ⅛ etc. — treat as quantity chars
const UNICODE_FRACTIONS = '½¼¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞⅐⅑⅒';

// numeric value of each unicode fraction glyph
const UNICODE_FRACTION_VALUES = new Map<string, number>([
	['½', 0.5], ['¼', 0.25], ['¾', 0.75],
	['⅓', 1 / 3], ['⅔', 2 / 3],
	['⅕', 0.2], ['⅖', 0.4], ['⅗', 0.6],
	['⅘', 0.8], ['⅙', 1 / 6], ['⅚', 5 / 6],
	['⅛', 0.125], ['⅜', 0.375], ['⅝', 0.625],
	['⅞', 0.875], ['⅐', 1 / 7], ['⅑', 1 / 9],
	['⅒', 0.1],
]);

// quantity capture (group 1 = number without the times-x); ordering matters (mixed/range before plain)
const QTY = new RegExp(
	'(?<![\\p{L}\\p{N}])('
	+ '\\d+\\s+\\d+/\\d+'                                       // mixed  1 1/2
	+ '|\\d+\\s+[' + UNICODE_FRACTIONS + ']'                    // mixed  1 ½ (space between numbers)
	+ '|\\d+(?:[.,]\\d+)?\\s*[-\\u2013]\\s*\\d+(?:[.,]\\d+)?'   // range  2-3
	+ '|\\d+/\\d+'                                              // simple 3/4
	+ '|\\d*[' + UNICODE_FRACTIONS + ']'                        // 1½ or ½
	+ '|\\d+(?:[.,]\\d+)?'                                      // 200 / 0,5
	+ ')(?:\\s*[xX\\u00d7](?![\\p{L}\\p{N}]))?', 'u');          // optional "times" x / ×

const RANGE = /(\d+(?:[.,]\d+)?)\s*[-–]\s*(\d+(?:[.,]\d+)?)/;

// prepositions that link a quantity+unit to the ingredient ("500 g de farine", "30 gr di olio",
// "2 c. à soupe d'huile", "2 cups of flour"). Locale-agnostic on purpose. Both apostrophes, the
// typewriter one and the typographic one real data is full of ("2 gousses d’ail"), and no word
// boundary required after the elision — \b would not fire before an accented letter ("d'épinards").
// Repeatable, because French writes one on each side of the unit ("¼ de tasse de sel").
// No /u flag: \b must stay ASCII-word based so "de" is not stripped from "deux".
const LEADING_PREPOSITION = /^(?:(?:de|di|of)\b\s*|d['’]\s*)+/i;

// plural marker our own ingredient input writes after the unit word: "1 pincée(s) Sel", "1 Prise(n) Salz",
// "1 cuillère(s) à soupe". Stripped BEFORE unit matching (only when a quantity was found): left in place it
// splits multi-word units — "cuillère(s) à soupe" would only match "cuillère" and leave "à soupe" in the name.
const PLURAL_MARKER = /\((?:s|n|e|en|es)\)/gi;

// what Double.parseDouble accepts, minus the exotic forms (hex, trailing d/f)
const NUMERIC = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

// spelled-out small quantities per locale (keys already normalized: lowercase, accent-free —
// "één" -> "een"). Limited to 1/2/3 on purpose. Used only when no digit quantity is found.
const WORD_NUMBERS = new Map<string, Map<string, number>>([
	['en', new Map([['one', 1], ['two', 2], ['three', 3]])],
	['fr', new Map([['un', 1], ['une', 1], ['deux', 2], ['trois', 3]])],
	['de', new Map([['ein', 1], ['eine', 1], ['eins', 1], ['zwei', 2], ['drei', 3]])],
	['es', new Map([['uno', 1], ['una', 1], ['dos', 2], ['tres', 3]])],
	['it', new Map([['uno', 1], ['una', 1], ['due', 2], ['tre', 3]])],
	['nl', new Map([['een', 1], ['twee', 2], ['drie', 3]])],
]);

// the same words as whole-word patterns, compiled once: locale -> [pattern, value][]
const WORD_NUMBER_PATTERNS = new Map<string, [RegExp, number][]>(
	[...WORD_NUMBERS].map(([locale, words]) =>
		[locale, [...words].map(([word, value]) => [wholeWord(word), value] as [RegExp, number])]));

// when no locale hint is given, try these in order for both word-numbers and unit preference
const LOCALE_ORDER = ['en', 'fr', 'de', 'es', 'it', 'nl'];

export class IngredientParser {

	units: UnitRegistry;
	// all known unit forms, longest-first so "c. à soupe" wins over "c."
	#aliases: UnitAlias[];

	constructor(units: UnitRegistry = UnitRegistry.get()) {
		this.units = units;
		this.#aliases = buildAliases(units);
	}

	/**
	 * @param line
	 * @param localeHint a 2-letter language code (en/fr/de/es/it/nl) preferred when resolving
	 *        word-numbers and units; null/unknown falls back to LOCALE_ORDER.
	 */
	parse(line: string, localeHint: string | null = null): ParsedIngredient {
		if (line == null || !line.trim()) {
			return { name: '', quantity: null, unit: null };
		}
		let text = line.trim();
		const locales = localeOrder(localeHint);

		// 1) quantity anywhere; drop its span (and any trailing times-x) from the text
		let quantity: number | null = null;
		let anchor = -1; // where the quantity was: the unit is expected next to it
		const q = QTY.exec(text);
		if (q) {
			quantity = toNumber(q[1]);
			anchor = q.index + 1;
			text = text.slice(0, q.index) + ' ' + text.slice(q.index + q[0].length);
		} else {
			// no digit quantity: try a spelled-out small number (one/deux/tre...) in preferred locale order
			const w = findWordNumber(text, locales);
			if (w) {
				quantity = w.value;
				anchor = w.start + 1;
				text = text.slice(0, w.start) + ' ' + text.slice(w.end);
			}
		}

		// 2) the unit nearest the quantity; drop its span too. No quantity means no unit: the unit word is
		// then part of the name ("Pinch of salt", "Noodle packet") and cutting it out only mangles the name.
		let unit: string | null = null;
		let unitAsWritten: string | null = null; // kept for the "the ingredient IS the unit word" case below
		let untranslatedUnit = false;
		if (quantity != null) {
			text = text.replace(PLURAL_MARKER, '');
			const hit = findUnit(this.#aliases, text, locales, anchor);
			if (hit) {
				unit = hit.canonical;
				untranslatedUnit = !hit.translated;
				unitAsWritten = text.slice(hit.start, hit.end).trim();
				text = text.slice(0, hit.start) + ' ' + text.slice(hit.end);
			} else {
				// 3) a bare count is a count of things: "2 pâtes feuilletées" -> 2 pc. A quantity never ships
				// without a unit.
				unit = PIECE;
			}
		}

		let name = text.replace(/\s+/g, ' ').trim();
		// 4) a leading preposition ("de"/"di"/"d'"/"of") is just a link word between the quantity and the name
		if (quantity != null) {
			name = name.replace(LEADING_PREPOSITION, '');
		}
		// 5) nothing left as a name: the ingredient IS the unit word ("2 Knoblauchzehen", "4 cloves"). Give the
		// word back as the name and count pieces. Only for an untranslated unit — a bare "300 g" is a nameless
		// junk line, and "g" would be a worse name than none.
		if (!name && untranslatedUnit) {
			name = unitAsWritten;
			unit = PIECE;
		}
		return { name, quantity, unit };
	}
}

let sharedParser: IngredientParser;

/** Building the alias table costs a few hundred regexps, only pay it once. */
export function getIngredientParser(): IngredientParser {
	sharedParser ??= new IngredientParser();
	return sharedParser;
}

/**
 * Resolve a unit written by the user in the quantity picker: a known form is canonicalized ("grams"
 * -> "g", "bouteilles" -> "bouteille"), anything else is registered so that a line carrying it
 * ("2kist flour") still reads back as a quantity. Session-local, the server stores the unit on the
 * item and not the list of units.
 * @returns the unit name to store.
 */
export function registerUnit(written: string, localeHint: string | null = null): string {
	const
		registry = UnitRegistry.get(),
		locale = localeHint || 'en',
		unitName = written.trim(),
		//ambiguous forms ("el" is both de and nl for tbsp) all point to the same unit
		[known] = registry.findUnitNames(unitName);

	//a unitKey is nobody's spelling, so no form points to it, but an item may well carry one: that is
	//what an untranslated unit used to be stored as here, and what a client that localizes them sends
	if (known || registry.definition(unitName)) {
		return registry.output(known || unitName, locale);
	}
	if (registry.addUnit(unitName, locale)) {
		//the shared parser caches an alias table built from the registry, it has to be rebuilt
		sharedParser = undefined;
	}
	return unitName;
}

/**
 * A line whose quantity is written at the head: "300g flour" -> {name:'flour', quantity:300, unit:'g'}.
 * @returns null when the line carries no such quantity. The name may be empty ("300g").
 */
export function parseLeadingQuantity(line: string, localeHint: string | null = null): ParsedIngredient | null {
	const parsed = getIngredientParser().parse(line, localeHint);
	// parse() takes a quantity from anywhere in the line, which mangles free text like "Milk 2%" into
	// "Milk %". Only trust it when it consumed the head and left the item name untouched.
	if (parsed.quantity == null || !line.trim().endsWith(parsed.name)) {
		return null;
	}
	return parsed;
}

// prefer the hinted locale, then the default order; unknown/null hint just uses the default order.
function localeOrder(hint: string | null): string[] {
	const h = hint == null ? null : hint.trim().toLowerCase();
	if (!h || !WORD_NUMBERS.has(h) || LOCALE_ORDER[0] === h) {
		return LOCALE_ORDER;
	}
	return [h, ...LOCALE_ORDER.filter((l) => l !== h)];
}

// spelled-out 1/2/3, tried locale by locale in preference order; whole-word, accent/case insensitive.
function findWordNumber(text: string, locales: string[]): {value: number, start: number, end: number} | null {
	const n = normalizeWithMap(text);
	for (const loc of locales) {
		for (const [pattern, value] of WORD_NUMBER_PATTERNS.get(loc) ?? []) {
			const m = pattern.exec(n.text);
			if (m) {
				const end = m.index + m[0].length;
				return { value, start: n.map[m.index], end: end < n.map.length ? n.map[end] : text.length };
			}
		}
	}
	return null;
}

// find the unit phrase belonging to the quantity at anchor: see score(). Match indices are mapped back
// to the original string so accents/case are preserved in the name.
function findUnit(aliases: UnitAlias[], text: string, locales: string[], anchor: number)
		: {canonical: string, translated: boolean, start: number, end: number} | null {
	const n = normalizeWithMap(text);
	let best: UnitAlias | null = null;
	let bestScore = Number.MAX_SAFE_INTEGER;
	let bestStart = -1;
	let bestEnd = -1;
	for (const a of aliases) {
		const m = a.pattern.exec(n.text);
		if (!m) {
			continue;
		}
		const
			start = n.map[m.index],
			end = m.index + m[0].length,
			candidate = score(text, start, anchor, a, locales);

		if (candidate < bestScore) {
			best = a;
			bestScore = candidate;
			bestStart = start;
			bestEnd = end < n.map.length ? n.map[end] : text.length;
		}
	}
	if (best === null) {
		return null;
	}
	return {
		canonical: unitOutput(best.def, winningLocale(best.locales, locales)),
		translated: best.def.translated,
		start: bestStart,
		end: bestEnd,
	};
}

/**
 * rank a unit candidate, lowest wins, on four criteria in decreasing importance:
 *   1. a candidate right after ANOTHER number is a size annotation, not the ingredient's unit — the
 *      real quantity is already cut out, so any number still there belongs to someone else:
 *      "1 (14.5 ounce) can tomatoes" -> can, not oz. It only loses to another candidate though: when
 *      it is the only one it is still the best guess ("Farine T55 250 g" -> g).
 *   2. then the candidate closest to the quantity, which is where the unit of a quantity is written:
 *      "60 ml (1/4 tasse) de lait" -> ml, "200 g Biscuit à la cuillère" -> g.
 *   3. then the longest alias, which is what settles candidates starting at the same place
 *      ("c." inside "c. à soupe").
 *   4. then the preferred locale ("el" is both German and Dutch for a tablespoon).
 */
function score(text: string, start: number, anchor: number, a: UnitAlias, locales: string[]): number {
	const
		penalty = afterAnotherNumber(text, start) ? 1 : 0,
		distance = Math.min(Math.abs(start - anchor), 999),
		length = 99 - Math.min(a.key.length, 99),
		rank = Math.min(localeRank(a.locales, locales), 9);

	return penalty * 10000000 + distance * 10000 + length * 100 + rank;
}

// is there a number just before this candidate? "(14.5 ounce)", "15-oz.", "(400g)"
function afterAnotherNumber(text: string, start: number): boolean {
	let i = start - 1;
	while (i >= 0 && (text[i] === ' ' || text[i] === '-' || text[i] === '.' || text[i] === '/')) {
		i--;
	}
	return i >= 0 && /\p{Nd}/u.test(text[i]);
}

// which locale's spelling was matched: the most preferred one declaring this form, else the first
// declaring locale (forms only declared in sv/fi/pt/ro are not in the preferred order).
function winningLocale(aliasLocales: Set<string>, preferred: string[]): string {
	for (const l of preferred) {
		if (aliasLocales.has(l)) {
			return l;
		}
	}
	return aliasLocales.values().next().value;
}

// best (lowest) position of any of the alias's locales in the preferred order; unmatched = MAX.
function localeRank(aliasLocales: Set<string>, preferred: string[]): number {
	let best = Number.MAX_SAFE_INTEGER;
	for (const l of aliasLocales) {
		const idx = preferred.indexOf(l);
		if (idx >= 0 && idx < best) {
			best = idx;
		}
	}
	return best;
}

/**
 * Convert a quantity string to a number: mixed fractions ("1 1/2"->1.5), simple fractions ("3/4"->0.75),
 * unicode fractions ("½"->0.5, "1½"->1.5), decimal comma ("0,5"->0.5) and ranges ("2-3"->2.5, midpoint).
 * @returns null if nothing numeric could be extracted.
 */
function toNumber(raw: string): number | null {
	if (raw == null || !raw.trim()) {
		return null;
	}
	const range = RANGE.exec(raw);
	if (range) {
		return (parseSimple(range[1]) + parseSimple(range[2])) / 2;
	}
	let total = 0;
	let any = false;
	for (const part of raw.trim().split(/\s+/)) {
		const v = parsePart(part);
		if (v != null) {
			total += v;
			any = true;
		}
	}
	return any ? total : null;
}

/** one whitespace-separated token: "1/2", "3", "0.5", "½", "1½" */
function parsePart(part: string): number | null {
	let sum = 0;
	let any = false;
	let digits = '';
	for (const c of part) {
		const frac = UNICODE_FRACTION_VALUES.get(c);
		if (frac !== undefined) {
			sum += frac;
			any = true;
		} else {
			digits += c;
		}
	}
	const rest = digits.trim();
	if (rest) {
		const v = parseSimple(rest);
		if (v != null) {
			sum += v;
			any = true;
		}
	}
	return any ? sum : null;
}

/** "3/4" -> 0.75, "3" -> 3, "0,5" -> 0.5; null if not numeric */
function parseSimple(s: string): number | null {
	s = s.replace(/,/g, '.');
	const slash = s.indexOf('/');
	if (slash > 0) {
		const num = toDouble(s.slice(0, slash));
		const den = toDouble(s.slice(slash + 1));
		if (num == null || den == null) {
			return null;
		}
		return den === 0 ? null : num / den;
	}
	return toDouble(s);
}

function toDouble(s: string): number | null {
	s = s.trim();
	return NUMERIC.test(s) ? Number(s) : null;
}

// ---- normalization (accent-strip + lowercase) -------------------------------------------------

/**
 * normalize while keeping, for every normalized char, the index of the original char it came from.
 * e.g. "Curaçao" -> text "curacao", map [0,1,2,3,4,5,6] (each normalized char points back to its source);
 * a char that expands keeps pointing at its origin, so a match on the normalized copy can be sliced
 * out of the ORIGINAL string (preserving its accents/case) using map[matchStart]..map[matchEnd].
 */
function normalizeWithMap(s: string): {text: string, map: number[]} {
	let text = '';
	const map: number[] = [];
	let i = 0;
	for (const ch of s) {
		const d = normalize(ch);
		for (const c of d) {
			text += c;
			map.push(i);
		}
		i += ch.length;
	}
	return { text, map };
}

/**
 * strip accents/diacritics and lowercase so lookups are accent- and case-insensitive.
 * e.g. "Cuillères À Soupe" -> "cuilleres a soupe", "Olivenöl" -> "olivenol"
 */
function normalize(s: string): string {
	return s.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase();
}

/** normalize a written unit form, which is where the spaces around it are noise. */
function normalizeForm(s: string): string {
	return normalize(s.trim());
}

/**
 * whole-word match over normalized text.
 *   (?<!...) = negative lookbehind: assert the char BEFORE is NOT a letter/digit
 *   \p{L} = any Unicode letter, \p{N} = any Unicode digit
 *   escapeRegExp(word) = the text taken literally (a "." in "c." matches a real dot, not any-char)
 *   (?!...) = negative lookahead: assert the char AFTER is NOT a letter/digit
 * Net effect: "g" matches "200 g flour" but NOT the "g" inside "ginger".
 */
function wholeWord(word: string): RegExp {
	return new RegExp('(?<![\\p{L}\\p{N}])' + escapeRegExp(word) + '(?![\\p{L}\\p{N}])', 'u');
}

function escapeRegExp(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * A unit form ready to be matched: its regex over normalized text, its normalized key, the unit it
 * belongs to, and the locales that declare this form (for locale-preferred tie-breaking, and to pick
 * the output spelling).
 */
type UnitAlias = {
	pattern: RegExp;
	key: string;
	def: UnitDef;
	locales: Set<string>;
};

/**
 * build the flat list of unit alias patterns from the registry, sorted longest-first.
 * e.g. the tbsp def contributes "tbsp", "cuillere a soupe", "c. a soupe", "el", "cs", ...
 * all mapped to the tbsp unit; sorting longest-first makes "c. a soupe" match before "c."
 */
function buildAliases(units: UnitRegistry): UnitAlias[] {
	// norm form -> (unit + declaring locales); the FIRST unit declaring a form wins when several do,
	// but EVERY locale of that unit declaring it is recorded. e.g. "el" is both German tablespoon and
	// Dutch tablespoon -> unit tbsp, locales {de, nl}.
	const byNorm = new Map<string, {def: UnitDef, locales: Set<string>}>();
	for (const def of units.definitions()) {
		for (const [locale, value] of Object.entries(def.values)) {
			for (const form of value.eq) {
				const key = normalizeForm(form);
				if (!key) {
					continue;
				}
				if (!byNorm.has(key)) {
					byNorm.set(key, { def, locales: new Set() });
				}
				const info = byNorm.get(key);
				//a form stolen by another unit keeps that unit's locales, not this one's
				if (info.def === def) {
					info.locales.add(locale);
				}
			}
		}
	}
	return [...byNorm.entries()]
		.sort((a, b) => b[0].length - a[0].length) // longest first
		.map(([key, info]) => ({ pattern: wholeWord(key), key, def: info.def, locales: info.locales }));
}
